import { createTheme, type Theme, type ThemeOptions } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';

export const colors = {
	red: '#CE1126',
	blue: '#003F87',
	green: '#006B3F',
	brown: '#996633',
	yellow1: '#FFCC00',
	yellow2: '#FCD116',
	black: process.env.REACT_APP_PRELOAD_BG_DARK || '#161C24',
	stBlack: '#212B36',
	white: process.env.REACT_APP_PRELOAD_BG_LIGHT || '#F4F6F8',
	offWhite: '#F4F6F8',
	stWhite: '#F4F6F8',
	brightWhite: '#FFF',
	lightBG: '#F4F6F8',
	darkBG: '#161C24',
	lightTextPrimary: '#212B36',
	lightTextSecondary: '#637381',
	darkTextPrimary: '#EDF2F7',
	darkTextSecondary: '#919EAB',
} as const;

export type ColorKey = keyof typeof colors;

/**
 * Chromium/PWA chrome = exact page/navbar surfaces (FSB/JA pattern).
 * Never use brand accent (Venturing Green) — that paints a solid green title bar.
 */
export const getChromeThemeColor = (darkMode: boolean, _primaryColor = 'green'): string =>
	darkMode ? colors.darkBG : colors.lightBG;

/** Relative luminance for WCAG contrast (sRGB hex). */
const relativeLuminance = (hex: string): number => {
	const normalized = hex.replace('#', '');
	const channels = normalized.match(/.{2}/g)?.map((pair) => parseInt(pair, 16) / 255) ?? [0, 0, 0];
	const [r, g, b] = channels.map((channel) =>
		channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4,
	);
	return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const contrastRatio = (foreground: string, background: string): number => {
	const fg = relativeLuminance(foreground);
	const bg = relativeLuminance(background);
	const lighter = Math.max(fg, bg);
	const darker = Math.min(fg, bg);
	return (lighter + 0.05) / (darker + 0.05);
};

const MIN_CONTENT_CONTRAST = 4.5;
let minContentContrastThreshold = MIN_CONTENT_CONTRAST;

/** Test-only: force contrast fallback paths by raising/resetting the WCAG threshold. */
export const __setMinContentContrastForTests = (value: number | null) => {
	minContentContrastThreshold = value == null ? MIN_CONTENT_CONTRAST : value;
};

/** Pick a readable accent for icons/eyebrows on neutral section surfaces. */
export const resolveContentAccent = (mode: 'light' | 'dark', primaryKey: string): string => {
	const surface = mode === 'dark' ? colors.darkBG : colors.lightBG;
	const primary = colors[primaryKey as ColorKey] || colors.green;
	const candidates = [
		primary,
		colors.blue,
		colors.green,
		colors.red,
		colors.brown,
		mode === 'dark' ? colors.darkTextPrimary : colors.lightTextPrimary,
		mode === 'dark' ? colors.yellow2 : colors.brown,
	];

	for (const candidate of candidates) {
		if (contrastRatio(candidate, surface) >= minContentContrastThreshold) {
			return candidate;
		}
	}

	return mode === 'dark' ? colors.darkTextPrimary : colors.lightTextPrimary;
};

/** Pick a readable color for inline links on neutral section surfaces. */
export const resolveContentLink = (mode: 'light' | 'dark', primaryKey: string): string =>
	resolveContentAccent(mode, primaryKey);

declare module '@mui/material/styles' {
	interface Palette {
		contentAccent: { main: string };
		contentLink: { main: string };
	}
	interface PaletteOptions {
		contentAccent?: { main: string };
		contentLink?: { main: string };
	}
}

const commonCustomColors = {
	custom: {
		brown: colors.brown,
		red: colors.red,
		black: colors.black,
		white: colors.white,
        offWhite: colors.offWhite,
		brightWhite: colors.brightWhite,
		yellow1: colors.yellow1,
		yellow2: colors.yellow2,
		blue: colors.blue,
		green: colors.green,
		stwhite: colors.stWhite,
		stblack: colors.stBlack,
		lightBG: colors.lightBG,
        darkBG: colors.darkBG,
        lightTextPrimary: colors.lightTextPrimary,
        lightTextSecondary: colors.lightTextSecondary,
        darkTextPrimary: colors.darkTextPrimary,
        darkTextSecondary: colors.darkTextSecondary,
	},
};

interface DataGridStyleOverrides {
	root: Record<string, unknown>;
}

const getDataGridStyles = (mode: string, mainColor: string): DataGridStyleOverrides => {
	const isLight = mode === 'light';
	const cellColor = isLight ? colors.lightTextPrimary : colors.darkTextPrimary;
	const headerBg = isLight ? mainColor : colors.stBlack;
	const rowBorderColor = isLight ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)';

	return {
		root: {
			'& .MuiDataGrid-cell': {
				color: cellColor,
				borderColor: rowBorderColor,
				display: 'flex',
				alignItems: 'center',
				'& .MuiTypography-root': { color: 'inherit' },
			},
			'& .MuiDataGrid-cellContent': {
				display: 'flex',
				alignItems: 'center',
				width: '100%',
				minWidth: 0,
				lineHeight: 'normal',
			},
			'& .MuiDataGrid-columnHeader': {
				display: 'flex',
				alignItems: 'center',
			},
			'& .MuiDataGrid-columnHeaders': {
				backgroundColor: headerBg,
				color: colors.brightWhite,
				borderColor: rowBorderColor,
			},
			'& .MuiDataGrid-columnHeaderTitle': { color: colors.brightWhite },
			'& .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIconButton, & .MuiDataGrid-iconButtonContainer .MuiIconButton-root': {
				color: colors.brightWhite,
			},
			'& .MuiDataGrid-row:hover': {
				backgroundColor: isLight ? colors.offWhite : colors.stBlack,
				'& .MuiDataGrid-cell': { color: cellColor, fontWeight: 'bold' },
			},
			'& .MuiDataGrid-footerContainer': {
				color: cellColor,
				borderTopColor: rowBorderColor,
			},
			'& .MuiTablePagination-root, & .MuiTablePagination-selectIcon, & .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel': {
				color: cellColor,
			},
			'& .MuiDataGrid-selectedRowCount, & .MuiDataGrid-toolbarContainer': {
				color: cellColor,
			},
			'& .MuiCheckbox-root': {
				color: isLight ? colors.lightTextSecondary : colors.darkTextSecondary,
			},
		},
	};
};

const getDesignTokens = (mode: 'light' | 'dark', primaryColor: string): ThemeOptions => {
	const mainColor = colors[primaryColor as ColorKey] || colors.green;
	const contentAccent = resolveContentAccent(mode, primaryColor);
	const contentLink = resolveContentLink(mode, primaryColor);

	const commonConfig: ThemeOptions = {
		typography: {
			fontFamily: 'Nunito, Arial, sarif',
		},
		palette: {
			...commonCustomColors,
			contentAccent: { main: contentAccent },
			contentLink: { main: contentLink },
			success: { main: colors.green },
			error: { main: colors.red },
			warning: { main: colors.yellow1 },
			info: { main: colors.blue },
		},
		components: {
			MuiAlert: {
				styleOverrides: {
					root: { fontSize: '1.2rem', fontWeight: 'bold', padding: '1rem', borderRadius: '12px', textAlign: 'center' },
					standardSuccess: { backgroundColor: colors.green, color: colors.brightWhite },
					standardError: { backgroundColor: colors.red, color: colors.brightWhite },
					standardWarning: { backgroundColor: colors.yellow1, color: colors.black },
					standardInfo: { backgroundColor: colors.blue, color: colors.brightWhite },
				} as Record<string, unknown>,
			},
			MuiButton: {
				defaultProps: { variant: 'contained', size: 'medium' },
			},
			MuiLink: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						color: theme.palette.contentLink.main,
						'&:hover': {
							color: theme.palette.contentLink.main,
							opacity: 0.85,
						},
					}),
				},
			},
			MuiList: {
				styleOverrides: { root: { padding: 0 } },
			},
			MuiListItemIcon: {
				styleOverrides: { root: { minWidth: '30px', '&:hover': { color: colors.brightWhite } } },
			},
			MuiTableCell: {
				styleOverrides: { body: { fontSize: '15px' } },
			},
			MuiTableHead: {
				styleOverrides: { root: { '& th': { fontWeight: 'bold', fontSize: '16px' } } },
			},
			MuiTableBody: {
				styleOverrides: { root: { '& tr:last-child td, & tr:last-child th': { borderBottom: 'none' } } },
			},
			["MuiDataGrid" as string]: {
				styleOverrides: {
					root: {
						'& .MuiDataGrid-cell': { fontSize: '15px' },
						'& .MuiDataGrid-columnHeaderTitle': {
							fontSize: '15px',
                            fontFamily: 'Nunito, Arial, sarif',
							fontWeight: '500',
							letterSpacing: '0.5px',
						},
					},
				},
			},
		},
	};

	const lightConfig: ThemeOptions = {
		palette: {
			mode: 'light',
			primary: { main: mainColor },
			secondary: { main: colors.black },
			background: { main: colors.brightWhite, paper: colors.lightBG, default: colors.lightBG, passive: colors.white, seethru: colors.stWhite } as Record<string, string>,
			highlight: { main: mainColor },
			text: { main: colors.brightWhite, light: colors.white, dark: colors.black, active: colors.black, primary: colors.lightTextPrimary, secondary: colors.lightTextSecondary, highlight: mainColor } as Record<string, string>,
			boxShadow: '2px 4px 10px 1px rgba(0, 0, 0, 0.47)',
			DataGrid: {
				bg: colors.brightWhite,
				headerBg: mainColor,
				pinnedBg: colors.brightWhite,
			},
		} as Record<string, unknown>,
		components: {
			MuiCssBaseline: { styleOverrides: { body: { backgroundColor: colors.white, color: colors.lightTextPrimary } } },
			MuiTab: { styleOverrides: { root: { color: colors.black, '&.Mui-selected': { color: mainColor } } } },
			MuiTextField: { styleOverrides: { root: { '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: colors.black }, '&:hover fieldset': { borderColor: mainColor }, '&.Mui-focused fieldset': { borderColor: mainColor } } } } },
			MuiSelect: { styleOverrides: { root: { '& .MuiSvgIcon-root': { color: colors.black } }, select: { color: colors.black } } },
			MuiOutlinedInput: {
				styleOverrides: {
					root: { '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: mainColor }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: mainColor }, '.MuiOutlinedInput-notchedOutline': { borderColor: colors.black } },
					input: { '::placeholder': { color: colors.black, opacity: 1 } },
				},
			},
			MuiInputLabel: { styleOverrides: { root: { color: colors.black, '&.Mui-focused': { color: mainColor } } } },
			MuiButton: {
				styleOverrides: {
					contained: {
						backgroundColor: mainColor,
						color: colors.brightWhite,
						'&:hover': { backgroundColor: colors.black, color: colors.brightWhite },
						'&.Mui-disabled': {
							backgroundColor: mainColor,
							color: colors.brightWhite,
							opacity: 0.5,
						},
					},
					outlined: { borderColor: colors.black, color: colors.black, '&:hover': { borderColor: mainColor, color: mainColor } },
					text: { color: colors.black, '&:hover': { backgroundColor: mainColor, color: colors.brightWhite } },
				},
			},
			MuiCheckbox: { styleOverrides: { root: { color: colors.lightTextSecondary, '&.Mui-checked': { color: mainColor }, '&.MuiCheckbox-indeterminate': { color: mainColor } } } },
			MuiListItemButton: {
				styleOverrides: { root: { margin: '0px', padding: '0px 5px', color: colors.lightTextSecondary, '&:hover': { backgroundColor: mainColor } } },
			},
			MuiListItemIcon: { styleOverrides: { root: { color: colors.lightTextSecondary, '&:hover': { color: colors.black } } } },
			MuiListItemText: {
				styleOverrides: {
					primary: { fontSize: '14px', fontWeight: 'bold', color: colors.lightTextPrimary, '&:hover': { color: colors.black } },
					secondary: { fontSize: '14px', fontWeight: 'bold', color: colors.lightTextPrimary, '&:hover': { color: colors.black } },
				},
			},
			MuiTableCell: { styleOverrides: { body: { color: colors.lightTextPrimary } } },
			MuiTableHead: { styleOverrides: { root: { '& th': { color: colors.lightTextPrimary } } } },
			MuiDataGrid: {
				styleOverrides: getDataGridStyles(mode, mainColor),
			},
		} as ThemeOptions['components'],
	};

	const darkConfig: ThemeOptions = {
		palette: {
			mode: 'dark',
			primary: { main: mainColor },
			secondary: { main: colors.white },
			background: { main: colors.black, paper: colors.darkBG, default: colors.darkBG, seethru: colors.stBlack, passive: colors.stBlack } as Record<string, string>,
			highlight: { main: mainColor },
			text: { main: colors.white, active: colors.white, light: colors.white, dark: colors.black, primary: colors.darkTextPrimary, secondary: colors.darkTextSecondary, highlight: mainColor } as Record<string, string>,
			boxShadow: '2px 4px 10px 1px rgba(201, 201, 201, 0.47)',
			DataGrid: {
				bg: colors.darkBG,
				headerBg: colors.stBlack,
				pinnedBg: colors.darkBG,
			},
		} as Record<string, unknown>,
		components: {
			MuiCssBaseline: { styleOverrides: { body: { backgroundColor: colors.black, color: colors.darkTextPrimary } } },
			MuiTab: { styleOverrides: { root: { color: colors.white, '&.Mui-selected': { color: mainColor } } } },
			MuiTextField: { styleOverrides: { root: { '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: colors.white }, '&:hover fieldset': { borderColor: mainColor }, '&.Mui-focused fieldset': { borderColor: mainColor } } } } },
			MuiSelect: { styleOverrides: { root: { '& .MuiSvgIcon-root': { color: colors.white } }, select: { color: colors.white } } },
			MuiOutlinedInput: {
				styleOverrides: {
					root: { '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: mainColor }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: mainColor }, '.MuiOutlinedInput-notchedOutline': { borderColor: colors.brightWhite } },
					input: { '::placeholder': { color: colors.white, opacity: 1 } },
				},
			},
			MuiInputLabel: { styleOverrides: { root: { color: colors.white, '&.Mui-focused': { color: mainColor } } } },
			MuiButton: {
				styleOverrides: {
					contained: {
						backgroundColor: mainColor,
						color: colors.black,
						'&:hover': { backgroundColor: colors.white, color: colors.black },
						'&.Mui-disabled': {
							backgroundColor: mainColor,
							color: colors.black,
							opacity: 0.5,
						},
					},
					outlined: { borderColor: colors.white, color: colors.white, '&:hover': { borderColor: mainColor, color: mainColor } },
					text: { color: colors.white, '&:hover': { backgroundColor: mainColor, color: colors.white } },
				},
			},
			MuiCheckbox: { styleOverrides: { root: { color: colors.darkTextSecondary, '&.Mui-checked': { color: mainColor }, '&.MuiCheckbox-indeterminate': { color: mainColor } } } },
			MuiListItemButton: {
				styleOverrides: { root: { margin: '0px', padding: '0px 5px', '&:hover': { backgroundColor: colors.stBlack } } },
			},
			MuiListItemIcon: { styleOverrides: { root: { color: colors.darkTextSecondary, '&:hover': { color: colors.brightWhite } } } },
			MuiListItemText: {
				styleOverrides: {
					primary: { fontSize: '14px', fontWeight: 'bold', color: colors.darkTextSecondary, '&:hover': { color: colors.brightWhite } },
					secondary: { fontSize: '14px', fontWeight: 'bold', color: colors.darkTextSecondary, '&:hover': { color: colors.brightWhite } },
				},
			},
			MuiTableCell: { styleOverrides: { body: { color: colors.darkTextPrimary } } },
			MuiTableHead: { styleOverrides: { root: { '& th': { color: colors.darkTextPrimary } } } },
			MuiDataGrid: {
				styleOverrides: getDataGridStyles(mode, mainColor),
			},
		} as ThemeOptions['components'],
	};

	const modeSpecificConfig = mode === 'light' ? lightConfig : darkConfig;

	return deepmerge(commonConfig, modeSpecificConfig);
};

const theme = (darkMode: boolean, primaryColor: string): Theme => createTheme(getDesignTokens(darkMode ? 'dark' : 'light', primaryColor));

const LIGHT_BOX_SHADOW = '2px 4px 10px 1px rgba(0, 0, 0, 0.47)';
const DARK_BOX_SHADOW = '2px 4px 10px 1px rgba(201, 201, 201, 0.47)';

/** Box shadow token for layout chrome (layout context cannot rely on palette.mode with CSS vars). */
export const getBoxShadow = (darkMode: boolean): string => (darkMode ? DARK_BOX_SHADOW : LIGHT_BOX_SHADOW);

/**
 * Dual light/dark theme using CSS variables.
 * Mode switches via useColorScheme / html class — does not recreate the theme object.
 */
export const createAppTheme = (primaryColor: string): Theme => {
	const mainColor = colors[primaryColor as ColorKey] || colors.green;
	const lightAccent = resolveContentAccent('light', primaryColor);
	const darkAccent = resolveContentAccent('dark', primaryColor);
	const lightLink = resolveContentLink('light', primaryColor);
	const darkLink = resolveContentLink('dark', primaryColor);
	const lightDataGrid = getDataGridStyles('light', mainColor).root;
	const darkDataGrid = getDataGridStyles('dark', mainColor).root;

	return createTheme({
		cssVariables: {
			colorSchemeSelector: 'class',
		},
		colorSchemes: {
			light: {
				palette: {
					...commonCustomColors,
					mode: 'light',
					primary: { main: mainColor },
					secondary: { main: colors.black },
					background: {
						main: colors.brightWhite,
						paper: colors.lightBG,
						default: colors.lightBG,
						passive: colors.white,
						seethru: colors.stWhite,
					} as Record<string, string>,
					highlight: { main: mainColor },
					text: {
						main: colors.brightWhite,
						light: colors.white,
						dark: colors.black,
						active: colors.black,
						primary: colors.lightTextPrimary,
						secondary: colors.lightTextSecondary,
						highlight: mainColor,
					} as Record<string, string>,
					boxShadow: LIGHT_BOX_SHADOW,
					contentAccent: { main: lightAccent },
					contentLink: { main: lightLink },
					success: { main: colors.green },
					error: { main: colors.red },
					warning: { main: colors.yellow1 },
					info: { main: colors.blue },
					DataGrid: {
						bg: colors.brightWhite,
						headerBg: mainColor,
						pinnedBg: colors.brightWhite,
					},
				} as Record<string, unknown>,
			},
			dark: {
				palette: {
					...commonCustomColors,
					mode: 'dark',
					primary: { main: mainColor },
					secondary: { main: colors.white },
					background: {
						main: colors.black,
						paper: colors.darkBG,
						default: colors.darkBG,
						seethru: colors.stBlack,
						passive: colors.stBlack,
					} as Record<string, string>,
					highlight: { main: mainColor },
					text: {
						main: colors.white,
						active: colors.white,
						light: colors.white,
						dark: colors.black,
						primary: colors.darkTextPrimary,
						secondary: colors.darkTextSecondary,
						highlight: mainColor,
					} as Record<string, string>,
					boxShadow: DARK_BOX_SHADOW,
					contentAccent: { main: darkAccent },
					contentLink: { main: darkLink },
					success: { main: colors.green },
					error: { main: colors.red },
					warning: { main: colors.yellow1 },
					info: { main: colors.blue },
					DataGrid: {
						bg: colors.darkBG,
						headerBg: colors.stBlack,
						pinnedBg: colors.darkBG,
					},
				} as Record<string, unknown>,
			},
		},
		typography: {
			fontFamily: 'Nunito, Arial, sarif',
		},
		components: {
			MuiAlert: {
				styleOverrides: {
					root: { fontSize: '1.2rem', fontWeight: 'bold', padding: '1rem', borderRadius: '12px', textAlign: 'center' },
					standardSuccess: { backgroundColor: colors.green, color: colors.brightWhite },
					standardError: { backgroundColor: colors.red, color: colors.brightWhite },
					standardWarning: { backgroundColor: colors.yellow1, color: colors.black },
					standardInfo: { backgroundColor: colors.blue, color: colors.brightWhite },
				} as Record<string, unknown>,
			},
			MuiButton: {
				defaultProps: { variant: 'contained', size: 'medium' },
				styleOverrides: {
					contained: ({ theme }: { theme: Theme }) => ({
						backgroundColor: mainColor,
						color: colors.brightWhite,
						'&:hover': { backgroundColor: colors.black, color: colors.brightWhite },
						'&.Mui-disabled': {
							backgroundColor: mainColor,
							color: colors.brightWhite,
							opacity: 0.5,
						},
						...theme.applyStyles('dark', {
							color: colors.black,
							'&:hover': { backgroundColor: colors.white, color: colors.black },
							'&.Mui-disabled': {
								backgroundColor: mainColor,
								color: colors.black,
								opacity: 0.5,
							},
						}),
					}),
					outlined: ({ theme }: { theme: Theme }) => ({
						borderColor: colors.black,
						color: colors.black,
						'&:hover': { borderColor: mainColor, color: mainColor },
						...theme.applyStyles('dark', {
							borderColor: colors.white,
							color: colors.white,
							'&:hover': { borderColor: mainColor, color: mainColor },
						}),
					}),
					text: ({ theme }: { theme: Theme }) => ({
						color: colors.black,
						'&:hover': { backgroundColor: mainColor, color: colors.brightWhite },
						...theme.applyStyles('dark', {
							color: colors.white,
							'&:hover': { backgroundColor: mainColor, color: colors.white },
						}),
					}),
				},
			},
			MuiLink: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						color: theme.vars?.palette.contentLink.main ?? theme.palette.contentLink.main,
						'&:hover': {
							color: theme.vars?.palette.contentLink.main ?? theme.palette.contentLink.main,
							opacity: 0.85,
						},
					}),
				},
			},
			MuiList: {
				styleOverrides: { root: { padding: 0 } },
			},
			MuiListItemIcon: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						minWidth: '30px',
						color: colors.lightTextSecondary,
						'&:hover': { color: colors.black },
						...theme.applyStyles('dark', {
							color: colors.darkTextSecondary,
							'&:hover': { color: colors.brightWhite },
						}),
					}),
				},
			},
			MuiListItemButton: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						margin: '0px',
						padding: '0px 5px',
						color: colors.lightTextSecondary,
						'&:hover': { backgroundColor: mainColor },
						...theme.applyStyles('dark', {
							'&:hover': { backgroundColor: colors.stBlack },
						}),
					}),
				},
			},
			MuiListItemText: {
				styleOverrides: {
					primary: ({ theme }: { theme: Theme }) => ({
						fontSize: '14px',
						fontWeight: 'bold',
						color: colors.lightTextPrimary,
						'&:hover': { color: colors.black },
						...theme.applyStyles('dark', {
							color: colors.darkTextSecondary,
							'&:hover': { color: colors.brightWhite },
						}),
					}),
					secondary: ({ theme }: { theme: Theme }) => ({
						fontSize: '14px',
						fontWeight: 'bold',
						color: colors.lightTextPrimary,
						'&:hover': { color: colors.black },
						...theme.applyStyles('dark', {
							color: colors.darkTextSecondary,
							'&:hover': { color: colors.brightWhite },
						}),
					}),
				},
			},
			MuiTableCell: {
				styleOverrides: {
					body: ({ theme }: { theme: Theme }) => ({
						fontSize: '15px',
						color: colors.lightTextPrimary,
						...theme.applyStyles('dark', { color: colors.darkTextPrimary }),
					}),
				},
			},
			MuiTableHead: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						'& th': {
							fontWeight: 'bold',
							fontSize: '16px',
							color: colors.lightTextPrimary,
						},
						...theme.applyStyles('dark', {
							'& th': { color: colors.darkTextPrimary },
						}),
					}),
				},
			},
			MuiTableBody: {
				styleOverrides: { root: { '& tr:last-child td, & tr:last-child th': { borderBottom: 'none' } } },
			},
			MuiTab: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						color: colors.black,
						'&.Mui-selected': { color: mainColor },
						...theme.applyStyles('dark', {
							color: colors.white,
							'&.Mui-selected': { color: mainColor },
						}),
					}),
				},
			},
			MuiTextField: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						'& .MuiOutlinedInput-root': {
							'& fieldset': { borderColor: colors.black },
							'&:hover fieldset': { borderColor: mainColor },
							'&.Mui-focused fieldset': { borderColor: mainColor },
						},
						...theme.applyStyles('dark', {
							'& .MuiOutlinedInput-root': {
								'& fieldset': { borderColor: colors.white },
								'&:hover fieldset': { borderColor: mainColor },
								'&.Mui-focused fieldset': { borderColor: mainColor },
							},
						}),
					}),
				},
			},
			MuiSelect: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						'& .MuiSvgIcon-root': { color: colors.black },
						...theme.applyStyles('dark', {
							'& .MuiSvgIcon-root': { color: colors.white },
						}),
					}),
					select: ({ theme }: { theme: Theme }) => ({
						color: colors.black,
						...theme.applyStyles('dark', { color: colors.white }),
					}),
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						'&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: mainColor },
						'&:hover .MuiOutlinedInput-notchedOutline': { borderColor: mainColor },
						'.MuiOutlinedInput-notchedOutline': { borderColor: colors.black },
						...theme.applyStyles('dark', {
							'.MuiOutlinedInput-notchedOutline': { borderColor: colors.brightWhite },
						}),
					}),
					input: ({ theme }: { theme: Theme }) => ({
						'::placeholder': { color: colors.black, opacity: 1 },
						...theme.applyStyles('dark', {
							'::placeholder': { color: colors.white, opacity: 1 },
						}),
					}),
				},
			},
			MuiInputLabel: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						color: colors.black,
						'&.Mui-focused': { color: mainColor },
						...theme.applyStyles('dark', {
							color: colors.white,
							'&.Mui-focused': { color: mainColor },
						}),
					}),
				},
			},
			MuiCheckbox: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						color: colors.lightTextSecondary,
						'&.Mui-checked': { color: mainColor },
						'&.MuiCheckbox-indeterminate': { color: mainColor },
						...theme.applyStyles('dark', {
							color: colors.darkTextSecondary,
							'&.Mui-checked': { color: mainColor },
							'&.MuiCheckbox-indeterminate': { color: mainColor },
						}),
					}),
				},
			},
			MuiDataGrid: {
				styleOverrides: {
					root: ({ theme }: { theme: Theme }) => ({
						'& .MuiDataGrid-cell': { fontSize: '15px' },
						'& .MuiDataGrid-columnHeaderTitle': {
							fontSize: '15px',
							fontFamily: 'Nunito, Arial, sarif',
							fontWeight: '500',
							letterSpacing: '0.5px',
						},
						...lightDataGrid,
						...theme.applyStyles('dark', darkDataGrid),
					}),
				},
			},
		} as ThemeOptions['components'],
	});
};

export default theme;
